import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtINR, COUNTRIES } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import {
  ArrowRight, Sparkles, Globe2, Columns3, LineChart, MessageCircle, Trash2, Bookmark,
  Gauge, GraduationCap, BookOpen, Wallet, CheckCircle2, Pencil,
} from "lucide-react";

// ── Decision Desk card background illustrations ─────────────────────────────
// Each card uses an official supplied PNG (compressed to WebP, ~10-20KB each)
// rendered at ~10% opacity in the bottom-right with a left-side gradient
// protection so the title/description/CTA stay fully legible.
const CARD_ART_URL = {
  atlas:     "/cards/browse-universities.webp",
  compare:   "/cards/compare.webp",
  financial: "/cards/financial.webp",
  mentor:    "/cards/mentor.webp",
};

// Inline SVG art used as the Decision Engine card background. Compass +
// ranking podium = "ranked decisions" theme. Lightweight and tinted to the
// card's indigo accent.
function DecisionEngineArt({ tint = "#4F46E5" }) {
  return (
    <svg viewBox="0 0 220 220" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      {/* Compass */}
      <circle cx="70" cy="80" r="42" stroke={tint} strokeWidth="2.4" />
      <circle cx="70" cy="80" r="32" stroke={tint} strokeWidth="1" strokeOpacity="0.6" />
      <path d="M70 50 L78 80 L70 110 L62 80 Z" fill={tint} fillOpacity="0.85" />
      <path d="M70 50 L78 80 L70 80 Z" fill={tint} />
      <circle cx="70" cy="80" r="3" fill={tint} />
      {/* Compass cardinal ticks */}
      <path d="M70 40 V47 M70 113 V120 M30 80 H37 M103 80 H110" stroke={tint} strokeWidth="2" strokeLinecap="round" />
      {/* Ranking podium 1-2-3 */}
      <rect x="120" y="140" width="30" height="35" rx="3" stroke={tint} strokeWidth="2" />
      <rect x="150" y="115" width="30" height="60" rx="3" stroke={tint} strokeWidth="2" fill={tint} fillOpacity="0.10" />
      <rect x="180" y="130" width="30" height="45" rx="3" stroke={tint} strokeWidth="2" />
      <text x="133" y="162" fontSize="14" fontWeight="700" fill={tint} fontFamily="system-ui,sans-serif">2</text>
      <text x="163" y="148" fontSize="14" fontWeight="700" fill={tint} fontFamily="system-ui,sans-serif">1</text>
      <text x="193" y="156" fontSize="14" fontWeight="700" fill={tint} fontFamily="system-ui,sans-serif">3</text>
      {/* Star above the winner */}
      <path d="M165 100 L168 107 L175 108 L170 113 L171 120 L165 117 L159 120 L160 113 L155 108 L162 107 Z" fill={tint} fillOpacity="0.9" />
      {/* Decision trail */}
      <path d="M30 175 Q70 150 110 165 Q150 180 200 175" stroke={tint} strokeWidth="1.5" strokeDasharray="3 3" strokeOpacity="0.5" />
    </svg>
  );
}

// Inline SVG used only for the Student Readiness Profile section background.
function ProfileArt({ tint = "#4F46E5" }) {
  return (
    <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <circle cx="100" cy="75" r="28" stroke={tint} strokeWidth="2" />
      <path d="M50 165 Q50 115 100 115 Q150 115 150 165" stroke={tint} strokeWidth="2" fill="none" />
      <rect x="135" y="35" width="50" height="62" rx="6" stroke={tint} strokeWidth="1.6" />
      <path d="M143 50 H177 M143 60 H170 M143 70 H177 M143 80 H165" stroke={tint} strokeWidth="1.4" strokeLinecap="round" />
      <circle cx="160" cy="135" r="22" stroke={tint} strokeWidth="1.6" />
      <path d="M150 135 L157 142 L170 128" stroke={tint} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const MODULES = [
  { tag: "Top 10",  t: "Decision Engine",     d: "Safe / Target / Reach — ranked for your profile.",  to: "/top-picks",    id: "dash-card-top-picks",    icon: <Sparkles size={22} strokeWidth={1.5} />,      accentBar: "#4F46E5", iconBg: "#EEF2FF", iconFg: "#4F46E5", tagFg: "#4F46E5", artKey: "decisionEngine" },
  { tag: "Atlas",   t: "Browse universities", d: "80 curated institutions — 5+ per country.",         to: "/universities", id: "dash-card-universities", icon: <Globe2 size={22} strokeWidth={1.5} />,        accentBar: "#059669", iconBg: "#ECFDF5", iconFg: "#059669", tagFg: "#059669", artKey: "atlas" },
  { tag: "Compare", t: "Side-by-side",        d: "Up to four universities on one table.",             to: "/compare",      id: "dash-card-compare",      icon: <Columns3 size={22} strokeWidth={1.5} />,      accentBar: "#7C3AED", iconBg: "#F5F3FF", iconFg: "#7C3AED", tagFg: "#7C3AED", artKey: "compare" },
  { tag: "Plan",    t: "Financial plan",      d: "3–4 year projection with assumptions visible.",     to: "/financial",    id: "dash-card-financial",    icon: <LineChart size={22} strokeWidth={1.5} />,     accentBar: "#D97706", iconBg: "#FFFBEB", iconFg: "#D97706", tagFg: "#D97706", artKey: "financial" },
  { tag: "Mentor",  t: "Unimora AI Mentor",     d: "An AI advisor that explains its reasoning.",        to: "/advisor",      id: "dash-card-advisor",      icon: <MessageCircle size={22} strokeWidth={1.5} />, accentBar: "#C026D3", iconBg: "#FDF4FF", iconFg: "#C026D3", tagFg: "#C026D3", artKey: "mentor" },
];

export default function Dashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [profile, setProfile] = useState(null);
  const [recs, setRecs] = useState([]);
  const [shortlist, setShortlist] = useState([]);
  const [savedPlans, setSavedPlans] = useState([]);
  const [savedComparisons, setSavedComparisons] = useState([]);

  useEffect(() => {
    (async () => {
      const [p, r, s, sp, sc] = await Promise.all([
        api.get("/profile"),
        api.get("/recommendations"),
        api.get("/shortlist"),
        api.get("/saved-plans"),
        api.get("/saved-comparisons"),
      ]);
      setProfile(p.data);
      setRecs(r.data.results || []);
      setShortlist(s.data || []);
      setSavedPlans(sp.data || []);
      setSavedComparisons(sc.data || []);
    })();
  }, []);

  const deletePlan = async (plan_id) => {
    await api.delete(`/saved-plans/${plan_id}`);
    setSavedPlans(list => list.filter(p => p.plan_id !== plan_id));
  };
  const deleteComparison = async (comparison_id) => {
    await api.delete(`/saved-comparisons/${comparison_id}`);
    setSavedComparisons(list => list.filter(c => c.comparison_id !== comparison_id));
  };
  const openPlan = (p) => nav(`/financial?slug=${p.university_slug}&years=${p.duration_years}&inflation=${p.inflation_pct}`);
  const openComparison = (c) => nav(`/compare?slugs=${c.slugs.join(",")}`);

  const hasProfile = profile && profile.predicted_ib_score;
  const firstName = user?.name?.split(" ")[0] || "there";

  return (
    <div className="min-h-screen bg-white lg:pl-[240px]">
      <Nav />

      <div className="max-w-[1440px] mx-auto px-6 lg:px-10 pt-10 pb-10">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-6">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-micro font-semibold uppercase tracking-[0.05em] text-[var(--s500)]">Decision Desk</span>
            </div>
            <h1 className="mt-3 text-h1 text-[var(--s900)]" data-testid="dash-headline">
              Welcome back, <em className="italic text-[var(--ind)]">{firstName}.</em>
            </h1>
            <p className="text-body text-[var(--s600)] mt-3 max-w-xl leading-relaxed">
              {hasProfile
                ? "Recommendations are tailored to your profile. Tap any card to explore reasoning, costs and trade-offs."
                : "Complete your profile to unlock personalised recommendations, fit scores and tailored financial plans."}
            </p>
          </div>
          {!hasProfile && (
            <button className="btn-primary" onClick={() => nav("/profile")} data-testid="dash-build-profile">
              Build profile <ArrowRight size={14} strokeWidth={2.2} />
            </button>
          )}
        </div>

        {/* Modules */}
        <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-5 mt-10">
          {MODULES.map(m => {
            const artUrl = CARD_ART_URL[m.artKey];
            const isInlineArt = m.artKey === "decisionEngine";
            return (
              <button
                key={m.tag}
                onClick={() => nav(m.to)}
                className="group relative card p-6 text-left transition-all hover:-translate-y-0.5 hover:border-[var(--s300)] hover:shadow-md overflow-hidden"
                data-testid={m.id}
                style={{ background: m.iconBg }}
              >
                {/* Top accent bar */}
                <span
                  aria-hidden
                  data-testid={`${m.id}-accent`}
                  className="absolute top-0 left-0 right-0 h-1 z-[3]"
                  style={{ background: m.accentBar }}
                />
                {/* Background illustration. PNG via background-image for 4 cards,
                    inline SVG for the Decision Engine card. CSS-only hover glow:
                    opacity bumps + soft scale + slow ease-out transition (no JS). */}
                {(artUrl || isInlineArt) && (
                  <div
                    aria-hidden
                    data-testid={`${m.id}-art`}
                    className="absolute right-0 bottom-0 w-[68%] sm:w-[65%] h-full opacity-[0.22] sm:opacity-[0.22] group-hover:opacity-[0.34] group-hover:scale-[1.04] pointer-events-none z-[1] transition-all duration-500 ease-out"
                    style={isInlineArt ? {
                      filter: "saturate(1.05)",
                      transformOrigin: "right bottom",
                      maskImage: "linear-gradient(to left, black 60%, transparent 100%)",
                      WebkitMaskImage: "linear-gradient(to left, black 60%, transparent 100%)",
                    } : {
                      backgroundImage: `url('${artUrl}')`,
                      backgroundSize: "contain",
                      backgroundRepeat: "no-repeat",
                      backgroundPosition: "right bottom",
                      filter: "saturate(1.05)",
                      transformOrigin: "right bottom",
                      maskImage: "linear-gradient(to left, black 60%, transparent 100%)",
                      WebkitMaskImage: "linear-gradient(to left, black 60%, transparent 100%)",
                    }}
                  >
                    {isInlineArt && (
                      <div className="absolute inset-0 flex items-end justify-end p-2">
                        <div className="w-full h-full"><DecisionEngineArt tint={m.accentBar} /></div>
                      </div>
                    )}
                  </div>
                )}
                {/* Gradient protection overlay for text readability */}
                <span
                  aria-hidden
                  className="absolute inset-0 pointer-events-none z-[1]"
                  style={{ background: `linear-gradient(90deg, ${m.iconBg} 0%, ${m.iconBg} 38%, transparent 100%)` }}
                />
                {/* Content */}
                <div className="relative z-[2]">
                  <div className="flex items-center justify-between mb-4">
                    <span
                      className="w-10 h-10 rounded-full flex items-center justify-center bg-white shadow-sm"
                      style={{ color: m.iconFg }}
                    >
                      {m.icon}
                    </span>
                    <span
                      className="text-micro font-semibold tracking-[0.05em] uppercase"
                      style={{ color: m.tagFg }}
                    >
                      {m.tag}
                    </span>
                  </div>
                  <div className="text-h3 font-semibold text-[var(--s900)]">{m.t}</div>
                  <p className="text-body-sm leading-relaxed text-[var(--s600)] mt-1.5">{m.d}</p>
                  <div
                    className="mt-4 text-body-sm font-medium inline-flex items-center gap-1"
                    style={{ color: m.tagFg }}
                  >
                    Open <ArrowRight size={14} strokeWidth={2.2} />
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Student Readiness Profile */}
        <StudentReadinessProfile profile={profile} hasProfile={hasProfile} onEdit={() => nav("/profile")} />

        {/* Saved plans & comparisons */}
        {(savedPlans.length > 0 || savedComparisons.length > 0) && (
          <>
            <div className="flex items-center gap-2 mt-14 mb-3">
              <Bookmark size={14} strokeWidth={2.2} className="text-[var(--ind)]" />
              <span className="text-micro font-semibold tracking-[0.05em] uppercase text-[var(--s500)]">Saved by you</span>
            </div>
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <div className="text-body-sm font-semibold text-[var(--s700)] mb-3">Financial plans · {savedPlans.length}</div>
                {savedPlans.length === 0 ? (
                  <div className="card p-5 text-body-sm text-[var(--s500)]">
                    No saved plans yet. Build one in <button className="text-[var(--ind)] underline" onClick={() => nav("/financial")}>Financial</button>.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {savedPlans.map(p => (
                      <div
                        key={p.plan_id}
                        className="card p-4 flex items-center justify-between gap-3 hover:border-[var(--s300)] transition-colors"
                        data-testid={`saved-plan-${p.plan_id}`}
                      >
                        <button onClick={() => openPlan(p)} className="text-left flex-1 min-w-0">
                          <div className="text-body-lg font-semibold text-[var(--s900)] truncate">{p.label}</div>
                          <div className="text-label text-[var(--s500)] mt-0.5">
                            {p.country} · {p.duration_years} yr · {p.inflation_pct}% inflation
                          </div>
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); deletePlan(p.plan_id); }}
                          className="text-[var(--s400)] hover:text-[var(--danger)] p-1.5"
                          title="Delete"
                          data-testid={`saved-plan-del-${p.plan_id}`}
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <div className="text-body-sm font-semibold text-[var(--s700)] mb-3">Comparisons · {savedComparisons.length}</div>
                {savedComparisons.length === 0 ? (
                  <div className="card p-5 text-body-sm text-[var(--s500)]">
                    No saved comparisons yet. Build one in <button className="text-[var(--ind)] underline" onClick={() => nav("/compare")}>Compare</button>.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {savedComparisons.map(c => (
                      <div
                        key={c.comparison_id}
                        className="card p-4 flex items-center justify-between gap-3 hover:border-[var(--s300)] transition-colors"
                        data-testid={`saved-cmp-${c.comparison_id}`}
                      >
                        <button onClick={() => openComparison(c)} className="text-left flex-1 min-w-0">
                          <div className="text-body-lg font-semibold text-[var(--s900)] truncate">
                            {c.university_names.slice(0, 2).join(" vs ")}{c.university_names.length > 2 ? ` + ${c.university_names.length - 2}` : ""}
                          </div>
                          <div className="text-label text-[var(--s500)] mt-0.5">
                            {c.slugs.length} universities · {new Date(c.saved_at).toLocaleDateString()}
                          </div>
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); deleteComparison(c.comparison_id); }}
                          className="text-[var(--s400)] hover:text-[var(--danger)] p-1.5"
                          title="Delete"
                          data-testid={`saved-cmp-del-${c.comparison_id}`}
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* Shortlist */}
        {shortlist.length > 0 && (
          <>
            <div className="eyebrow mt-14 mb-3">Your shortlist</div>
            <div className="grid md:grid-cols-3 gap-5">
              {shortlist.map(u => (
                <div
                  key={u.slug}
                  onClick={() => nav(`/universities/${u.slug}`)}
                  className="card p-5 cursor-pointer transition-colors hover:border-[var(--s300)]"
                  data-testid={`short-${u.slug}`}
                >
                  <div className="text-body-lg font-semibold text-[var(--s900)] leading-snug">{u.name}</div>
                  <div className="text-label text-[var(--s500)] mt-1">{u.country} · {u.city}</div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Top fits */}
        <div className="mt-14 flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="eyebrow mb-2">Recommended for you</div>
            <h2 className="text-h2 font-semibold text-[var(--s900)]">Top fits, ranked with reasoning</h2>
          </div>
          <button className="btn-link" onClick={() => nav("/universities")} data-testid="dash-see-all">
            See all 80 universities →
          </button>
        </div>

        <div className="grid md:grid-cols-2 gap-5 mt-6">
          {recs.slice(0, 6).map(r => (
            <RecCard key={r.slug} r={r} onClick={() => nav(`/universities/${r.slug}`)} />
          ))}
        </div>
      </div>
      <Footer />
    </div>
  );
}

function StudentReadinessProfile({ profile, hasProfile, onEdit }) {
  const countries = (profile?.target_countries || [])
    .map(c => COUNTRIES.find(x => x.code === c)?.name)
    .filter(Boolean);
  const courses = profile?.intended_courses || [];
  const ib = profile?.predicted_ib_score;
  const budget = profile?.budget_inr_max
    ? `${fmtINR(profile.budget_inr_min || 0)} – ${fmtINR(profile.budget_inr_max)}`
    : null;

  return (
    <section className="mt-12" data-testid="student-readiness-profile">
      <div
        className="relative rounded-[var(--r24)] bg-white border border-[var(--s200)] shadow-sm overflow-hidden"
      >
        {/* Subtle illustration on the far right */}
        <div
          aria-hidden
          className="hidden md:block absolute right-0 top-0 w-[28%] h-full opacity-[0.07] pointer-events-none"
          style={{ maskImage: "linear-gradient(to left, black 50%, transparent 100%)", WebkitMaskImage: "linear-gradient(to left, black 50%, transparent 100%)" }}
        >
          <div className="w-full h-full p-6">
            <ProfileArt tint="#4F46E5" />
          </div>
        </div>

        <div className="relative p-6 md:p-7">
          {/* Header */}
          <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
            <div className="min-w-0">
              <h2 className="text-h2 font-semibold text-[var(--s900)] leading-tight">Student Readiness Profile</h2>
              <p className="text-body-sm text-[var(--s600)] mt-1.5">
                Your recommendations are personalized using these profile inputs.
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {hasProfile && (
                <span className="inline-flex items-center gap-1.5 text-micro font-semibold tracking-[0.05em] uppercase text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-1.5 rounded-full" data-testid="profile-ready-badge">
                  <CheckCircle2 size={12} strokeWidth={2.4} /> Profile ready for recommendations
                </span>
              )}
              <button
                onClick={onEdit}
                className="inline-flex items-center gap-1.5 text-body-sm font-semibold text-[var(--ind)] bg-[var(--ind-soft)] hover:bg-indigo-100 transition-colors px-3.5 py-2 rounded-[var(--r8)]"
                data-testid="dash-edit-profile"
              >
                <Pencil size={13} strokeWidth={2.2} /> {hasProfile ? "Edit Profile" : "Complete Profile"}
              </button>
            </div>
          </div>

          {/* Profile field tiles */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <ProfileTile
              icon={Gauge}
              label="Predicted IB"
              tone="indigo"
              value={ib ? <span className="font-semibold text-[var(--s900)]">{ib}<span className="text-[var(--s500)] font-normal">/45</span></span> : null}
            />
            <ProfileTile
              icon={GraduationCap}
              label="Grade"
              tone="emerald"
              value={profile?.grade ? <span className="font-medium text-[var(--s900)]">{profile.grade}</span> : null}
            />
            <ProfileTile
              icon={Globe2}
              label="Target Countries"
              tone="violet"
              value={
                countries.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5">
                    {countries.map(c => (
                      <span key={c} className="text-micro font-semibold text-violet-700 bg-violet-50 border border-violet-200 px-2 py-0.5 rounded-full">{c}</span>
                    ))}
                  </div>
                ) : null
              }
            />
            <ProfileTile
              icon={BookOpen}
              label="Intended Courses"
              tone="rose"
              value={
                courses.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5">
                    {courses.map(c => (
                      <span key={c} className="text-micro font-semibold text-rose-700 bg-rose-50 border border-rose-200 px-2 py-0.5 rounded-full">{c}</span>
                    ))}
                  </div>
                ) : null
              }
            />
            <ProfileTile
              icon={Wallet}
              label="Budget (INR)"
              tone="amber"
              value={budget ? <span className="font-medium text-[var(--s900)] text-body-sm leading-snug">{budget}</span> : null}
            />
          </div>
        </div>
      </div>
    </section>
  );
}

const TILE_TONES = {
  indigo:  { bg: "bg-[var(--ind-soft)]", fg: "text-[var(--ind)]" },
  emerald: { bg: "bg-emerald-50",        fg: "text-emerald-600" },
  violet:  { bg: "bg-violet-50",         fg: "text-violet-600" },
  rose:    { bg: "bg-rose-50",           fg: "text-rose-600" },
  amber:   { bg: "bg-amber-50",          fg: "text-amber-600" },
};

function ProfileTile({ icon: Icon, label, value, tone }) {
  const t = TILE_TONES[tone] || TILE_TONES.indigo;
  return (
    <div className="rounded-[var(--r16)] border border-[var(--s200)] bg-white p-4 flex flex-col gap-3 hover:border-[var(--s300)] transition-colors">
      <div className="flex items-center gap-2.5">
        <span className={`w-8 h-8 rounded-lg ${t.bg} ${t.fg} flex items-center justify-center shrink-0`}>
          <Icon size={15} strokeWidth={2} />
        </span>
        <span className="text-micro font-semibold tracking-[0.05em] uppercase text-[var(--s500)]">{label}</span>
      </div>
      <div className="text-body-sm leading-snug min-h-[1.5rem]">
        {value || <span className="text-[var(--s400)]">—</span>}
      </div>
    </div>
  );
}

function RecCard({ r, onClick }) {
  return (
    <div
      onClick={onClick}
      className="card p-6 cursor-pointer transition-all hover:-translate-y-0.5 hover:border-[var(--ind)] hover:shadow-md"
      data-testid={`rec-${r.slug}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-h3 font-semibold text-[var(--s900)] leading-snug">{r.name}</div>
          <div className="text-label text-[var(--s500)] mt-1">{r.city} · {r.country}</div>
        </div>
        <div className="text-right">
          <div className="text-h1 font-semibold text-[var(--ind)] leading-none" data-testid={`fit-score-${r.slug}`}>{Math.round(r.fit_score)}</div>
          <div className="text-micro font-semibold tracking-[0.05em] uppercase text-[var(--s400)] mt-1">Fit</div>
        </div>
      </div>
      <div className="mt-4 grid grid-cols-3 gap-3">
        <Bit k="QS" v={`#${r.qs_rank_2025}`} />
        <Bit k="Tuition/yr" v={fmtINR(r.tuition_per_year_inr)} />
        <Bit k="Empl." v={`${r.employment_rate_pct}%`} />
      </div>
      {r.reasons?.length > 0 && (
        <div className="mt-5 pt-4 border-t border-[var(--s200)]">
          <div className="text-micro font-semibold tracking-[0.05em] uppercase text-[var(--s400)] mb-1.5">Why</div>
          <p className="text-body-sm leading-relaxed text-[var(--s600)]">{r.reasons[0]}</p>
        </div>
      )}
    </div>
  );
}

function Bit({ k, v }) {
  return (
    <div>
      <div className="text-micro font-semibold tracking-[0.05em] uppercase text-[var(--s400)]">{k}</div>
      <div className="text-body-sm font-medium text-[var(--s900)] mt-0.5">{v}</div>
    </div>
  );
}
